import { useState } from 'react';
import { tasksData } from '../../../mocks/dashboard';
import { agencyInfo } from '../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

const priorityConfig = {
  high: { dot: 'bg-red-400', label: 'text-red-500', bg: 'bg-red-50' },
  medium: { dot: 'bg-amber-400', label: 'text-amber-600', bg: 'bg-amber-50' },
  low: { dot: 'bg-gray-300', label: 'text-gray-400', bg: 'bg-gray-100' },
};

export default function TasksWidget() {
  const [tasks, setTasks] = useState(tasksData);
  const [newTask, setNewTask] = useState('');
  const [showInput, setShowInput] = useState(false);

  const toggleTask = (id: string) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t))
    );
  };

  const addTask = () => {
    if (!newTask.trim()) return;
    setTasks((prev) => [
      ...prev,
      {
        id: `t${Date.now()}`,
        title: newTask,
        priority: 'medium' as const,
        time: 'Today',
        done: false,
        lead: '',
      },
    ]);
    setNewTask('');
    setShowInput(false);
  };

  const pending = tasks.filter((t) => !t.done).length;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-800">Today&apos;s Tasks</h2>
          <p className="text-xs text-gray-400 mt-0.5">{pending} pending</p>
        </div>
        <button
          onClick={() => setShowInput((v) => !v)}
          className="w-8 h-8 flex items-center justify-center rounded-xl text-white cursor-pointer transition-transform hover:scale-105"
          style={{ backgroundColor: brand }}
        >
          <i className="ri-add-line text-base" />
        </button>
      </div>

      {showInput && (
        <div className="mb-3 flex gap-2">
          <input
            type="text"
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTask()}
            placeholder="New task title..."
            className="flex-1 text-sm px-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-teal-300 bg-gray-50"
            autoFocus
          />
          <button
            onClick={addTask}
            className="text-xs font-medium px-3 py-2 rounded-xl text-white cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: brand }}
          >
            Add
          </button>
        </div>
      )}

      <div className="space-y-1 overflow-y-auto max-h-72">
        {tasks.map((task) => (
          <div
            key={task.id}
            className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${
              task.done ? 'opacity-50' : 'hover:bg-gray-50'
            }`}
          >
            <button
              onClick={() => toggleTask(task.id)}
              className="mt-0.5 w-4 h-4 flex items-center justify-center rounded flex-shrink-0 border-2 cursor-pointer transition-all"
              style={
                task.done
                  ? { backgroundColor: brand, borderColor: brand }
                  : { borderColor: '#D1D5DB' }
              }
            >
              {task.done && <i className="ri-check-line text-white text-[10px]" />}
            </button>
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm text-gray-700 leading-snug ${
                  task.done ? 'line-through text-gray-400' : ''
                }`}
              >
                {task.title}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-[11px] font-medium ${priorityConfig[task.priority].label}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${priorityConfig[task.priority].dot}`} />
                  {task.priority}
                </span>
                <span className="text-[11px] text-gray-400">{task.time}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-3 border-t border-gray-50">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-xs text-gray-400">Progress</p>
          <p className="text-xs font-semibold" style={{ color: brand }}>
            {tasks.length - pending}/{tasks.length}
          </p>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${((tasks.length - pending) / tasks.length) * 100}%`,
              backgroundColor: brand,
            }}
          />
        </div>
      </div>
    </div>
  );
}
